import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FiCheckCircle, FiAlertTriangle, FiArrowRight } from "react-icons/fi";
import "../styles/BookingAction.scss";
import { useLanguage } from "../context/LanguageContext";
import {
  resolveBookingAction,
  checkoutBookingAction,
  checkinBookingAction,
} from "../api/bookingActions";

const formatDate = (value, locale) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatDateTime = (value, locale) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getStatusMeta = (status, t) => {
  const normalized = String(status ?? "").toLowerCase();
  switch (normalized) {
    case "confirmed":
      return { label: t("bookingAction.status.reserved", "Reserved"), className: "status-pill--confirmed" };
    case "inuse":
      return { label: t("bookingAction.status.inUse", "In use"), className: "status-pill--inuse" };
    case "completed":
      return { label: t("bookingAction.status.completed", "Completed"), className: "status-pill--completed" };
    case "cancelled":
      return { label: t("bookingAction.status.cancelled", "Cancelled"), className: "status-pill--cancelled" };
    default:
      return {
        label: status ?? t("bookingAction.status.unknown", "Unknown"),
        className: "status-pill--default",
      };
  }
};

const getActionLabel = (nextAction, t) =>
  nextAction === "Checkout"
    ? t("bookingAction.actions.checkout", "Check out")
    : t("bookingAction.actions.returned", "Returned");

const BookingAction = () => {
  const { language, t } = useLanguage();
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const loadBooking = useCallback(async () => {
    if (!token) {
      setError(t("bookingAction.errors.missingToken", "Missing booking token."));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(null);

    try {
      const payload = await resolveBookingAction(token);
      setBooking(payload);
    } catch (err) {
      const message =
        err?.status === 410
          ? t("bookingAction.errors.expired", "This booking link has expired or been revoked.")
          : err?.status === 404
            ? t("bookingAction.errors.invalid", "This booking link is invalid.")
            : err?.message || t("bookingAction.errors.loadFailed", "We couldn't load this booking.");
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  const statusMeta = useMemo(() => getStatusMeta(booking?.status, t), [booking?.status, t]);
  const nextAction = booking?.nextAction ?? null;
  const actionLabel = nextAction ? getActionLabel(nextAction, t) : null;

  const handleOpenPin = () => {
    setPin("");
    setPinError("");
    setPinModalOpen(true);
  };

  const handleClosePin = () => {
    if (submitting) return;
    setPinModalOpen(false);
  };

  const handlePinChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(value);
  };

  const handleSubmitPin = async (event) => {
    event.preventDefault();
    if (!nextAction) return;
    if (pin.length < 4) {
      setPinError(t("bookingAction.errors.pinLength", "Enter the 4-6 digit partner PIN."));
      return;
    }

    setSubmitting(true);
    setPinError("");

    try {
      const actionCall = nextAction === "Checkout" ? checkoutBookingAction : checkinBookingAction;
      const payload = await actionCall(token, pin);
      setBooking(payload);
      setSuccess({
        label:
          nextAction === "Checkout"
            ? t("bookingAction.success.checkedOut", "Checked out")
            : t("bookingAction.success.checkedIn", "Checked in"),
        actionUtc: payload.actionUtc || new Date().toISOString(),
      });
      setPinModalOpen(false);
      setPin("");
    } catch (err) {
      setPinError(
        err?.status === 429
          ? t(
              "bookingAction.errors.tooManyAttempts",
              "Too many failed attempts. Please wait and try again."
            )
          : err?.message || t("bookingAction.errors.pinFailed", "PIN validation failed.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="booking-action-page">
      <div className="booking-action__frame">
        <header className="booking-action__hero">
          <p className="booking-action__eyebrow">
            {t("bookingAction.hero.eyebrow", "Wayli partner scan")}
          </p>
          <h1>{t("bookingAction.hero.title", "Booking QR action")}</h1>
          <p className="booking-action__subhead">
            {t(
              "bookingAction.hero.subhead",
              "This page shows a single booking. Actions require a verified partner PIN."
            )}
          </p>
        </header>

        {loading ? (
          <div className="booking-action__card booking-action__card--loading">
            <div className="booking-action__pulse" />
            <p>{t("bookingAction.loading", "Loading booking details...")}</p>
          </div>
        ) : error ? (
          <div className="booking-action__card booking-action__card--error">
            <FiAlertTriangle aria-hidden="true" />
            <h2>{t("bookingAction.errorTitle", "Unable to load booking")}</h2>
            <p>{error}</p>
          </div>
        ) : (
          <>
            {success && (
              <div className="booking-action__card booking-action__card--success">
                <FiCheckCircle aria-hidden="true" />
                <div>
                  <h2>
                    {success.label} {t("bookingAction.success.suffix", "successfully")}
                  </h2>
                  <p>{formatDateTime(success.actionUtc, language)}</p>
                </div>
              </div>
            )}

            <section className="booking-action__card booking-action__card--summary">
              <div className="booking-action__status">
                <span className={`status-pill ${statusMeta.className}`}>
                  {statusMeta.label}
                </span>
                {booking?.tokenExpiresUtc && (
                  <span className="booking-action__expires">
                    {t("bookingAction.summary.qrValidUntil", "QR valid until")}{" "}
                    {formatDateTime(booking.tokenExpiresUtc, language)}
                  </span>
                )}
              </div>
              <div className="booking-action__details">
                <div>
                  <h3>{t("bookingAction.summary.equipment", "Equipment")}</h3>
                  {(booking?.equipment ?? []).length ? (
                    <ul>
                      {(booking?.equipment ?? []).map((item, index) => (
                        <li key={`${item.name}-${index}`}>
                          {item.quantity} × {item.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>{t("bookingAction.summary.noEquipment", "No equipment listed.")}</p>
                  )}
                </div>
                <div>
                  <h3>{t("bookingAction.summary.time", "Time")}</h3>
                  <p>
                    {formatDate(booking?.startUtc, language)} · {formatTime(booking?.startUtc)} -{" "}
                    {formatTime(booking?.endUtc)}
                  </p>
                </div>
                <div>
                  <h3>{t("bookingAction.summary.pickupLocation", "Pickup location")}</h3>
                  <p>
                    {booking?.location?.name ||
                      t("bookingAction.summary.locationFallback", "Pickup location")}
                  </p>
                  {booking?.location?.address && (
                    <span>{booking.location.address}</span>
                  )}
                </div>
              </div>

              {nextAction ? (
                <button
                  type="button"
                  className="booking-action__cta"
                  onClick={handleOpenPin}
                >
                  {actionLabel}
                  <FiArrowRight aria-hidden="true" />
                </button>
              ) : (
                <p className="booking-action__note">
                  {t(
                    "bookingAction.summary.noActions",
                    "No actions are available for this booking right now."
                  )}
                </p>
              )}
            </section>
          </>
        )}
      </div>

      {pinModalOpen && (
        <div className="pin-modal" role="dialog" aria-modal="true">
          <div className="pin-modal__scrim" onClick={handleClosePin} />
          <div className="pin-modal__panel">
            <h2>{t("bookingAction.pin.title", "Partner PIN required")}</h2>
            <p>{t("bookingAction.pin.lead", "Enter the location partner PIN to confirm this action.")}</p>
            <form onSubmit={handleSubmitPin}>
              <label htmlFor="partner-pin">{t("bookingAction.pin.label", "Partner PIN")}</label>
              <input
                id="partner-pin"
                name="partnerPin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={pin}
                onChange={handlePinChange}
                maxLength={6}
                placeholder="••••"
                disabled={submitting}
              />
              {pinError && <span className="pin-modal__error">{pinError}</span>}
              <div className="pin-modal__actions">
                <button
                  type="button"
                  className="pin-modal__secondary"
                  onClick={handleClosePin}
                  disabled={submitting}
                >
                  {t("bookingAction.pin.cancel", "Cancel")}
                </button>
                <button type="submit" className="pin-modal__primary" disabled={submitting}>
                  {submitting
                    ? t("bookingAction.pin.checking", "Checking...")
                    : t("bookingAction.pin.confirm", "Confirm")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingAction;
